<template>
  <div class="form-builder-page">
    <!-- Header with save/cancel -->
    <div class="form-builder-header">
      <h3>
        {{
          isEditing ? t('registration.editForm') : t('registration.createForm')
        }}
      </h3>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <div class="tab-container">
        <button
          type="button"
          @click="activeTab = 'basic'"
          class="tab-btn"
          :class="{ active: activeTab === 'basic' }"
        >
          {{ t('registration.tabBasicInfo') }}
        </button>
        <button
          type="button"
          @click="activeTab = 'fields'"
          class="tab-btn"
          :class="{ active: activeTab === 'fields' }"
        >
          {{ t('registration.formFields') }}
        </button>

        <button
          type="button"
          @click="activeTab = 'translations'"
          class="tab-btn"
          :class="{ active: activeTab === 'translations' }"
        >
          {{ t('registration.formTranslations') }}
        </button>
        <button
          type="button"
          @click="activeTab = 'preview'"
          class="tab-btn"
          :class="{ active: activeTab === 'preview' }"
        >
          {{ t('registration.formPreview') }}
        </button>
        <button
          type="button"
          @click="activeTab = 'email'"
          class="tab-btn"
          :class="{ active: activeTab === 'email' }"
        >
          {{ t('registration.emailSettings') }}
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="form-builder-content">
      <!-- Basic Information Tab -->
      <div v-if="activeTab === 'basic'" class="tab-content">
        <div class="content-section basic-info-section">
          <h4>{{ t('registration.basicInformation') }}</h4>

          <div class="form-grid basic-info-grid">
            <div class="form-group">
              <label for="form-name">{{ t('registration.formName') }} *</label>
              <input
                id="form-name"
                v-model="formData.name"
                type="text"
                required
                :placeholder="t('registration.formNamePlaceholder')"
              />
            </div>

            <div class="form-group">
              <label for="form-title"
                >{{ t('registration.formTitle') }} *</label
              >
              <input
                id="form-title"
                v-model="formData.title"
                type="text"
                required
                :placeholder="t('registration.formTitlePlaceholder')"
              />
            </div>

            <div class="form-group full-width">
              <label for="form-description">{{
                t('registration.formDescription')
              }}</label>
              <textarea
                id="form-description"
                v-model="formData.description"
                rows="3"
                :placeholder="t('registration.formDescriptionPlaceholder')"
              ></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.isActive" />
                {{ t('registration.formActive') }}
              </label>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.multiAthlete"
                />
                {{ t('registration.multiAthleteRegistration') }}
              </label>
              <p class="help-text">{{ t('registration.multiAthleteHelp') }}</p>
            </div>

            <!-- Team Manager Fields Builder moved to Fields tab -->
          </div>
        </div>

        <!-- Public Registrants List Settings -->
        <div class="content-section basic-public-section">
          <h4>
            {{
              t('registration.publicRegistrationsSectionTitle') ||
              'Public Registrants List'
            }}
          </h4>
          <div class="form-grid basic-info-grid">
            <div class="form-group full-width">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.publicRegistrants.enabled"
                />
                {{
                  t('registration.publicRegistrantsEnable') ||
                  'Enable public registrants list'
                }}
              </label>
            </div>

            <template v-if="formData.settings.publicRegistrants.enabled">
              <div class="form-group full-width">
                <label>{{
                  t('registration.publicRegistrantsVisibleFields') ||
                  'Visible fields'
                }}</label>
                <div class="visible-fields">
                  <div
                    v-for="(key, idx) in publicVisibleKeys"
                    :key="key"
                    class="visible-field-row"
                  >
                    <span class="drag-index">{{ idx + 1 }}.</span>
                    <select v-model="publicVisibleKeys[idx]">
                      <option
                        v-for="opt in publicFieldOptions"
                        :key="opt.value"
                        :value="opt.value"
                        :disabled="isKeySelectedElsewhere(opt.value, idx)"
                      >
                        {{ opt.label }} ({{ opt.value }})
                      </option>
                    </select>
                    <input
                      class="label-override"
                      :placeholder="
                        t('registration.columnLabelOptional') ||
                        'Column label (optional)'
                      "
                      :value="publicColumnLabels[key] || ''"
                      @input="onColumnLabelChange(key, $event.target.value)"
                    />
                    <div
                      class="row-actions field-actions team-manager-field-actions"
                    >
                      <button
                        type="button"
                        class="btn-icon tm-field-icon-btn"
                        @click="movePublicKey(idx, -1)"
                        :disabled="idx === 0"
                        :title="t('general.moveUp') || 'Move Up'"
                      >
                        <svg
                          class="tm-field-svg"
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                        >
                          <path d="M8 3l-4 4h3v6h2V7h3L8 3z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="btn-icon tm-field-icon-btn"
                        @click="movePublicKey(idx, 1)"
                        :disabled="idx === publicVisibleKeys.length - 1"
                        :title="t('general.moveDown') || 'Move Down'"
                      >
                        <svg
                          class="tm-field-svg"
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                        >
                          <path d="M8 13l4-4H9V3H7v6H4l4 4z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="btn-icon tm-field-icon-btn btn-danger"
                        @click="removePublicKey(idx)"
                        :title="t('general.delete') || 'Delete'"
                      >
                        <svg
                          class="tm-field-svg"
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                        >
                          <path
                            d="M4.2 3l3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div class="actions-row">
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      @click="addPublicKey"
                    >
                      {{ t('button.add') }}
                    </button>
                  </div>
                </div>
                <p class="hint">
                  {{
                    t('registration.publicRegistrantsHint') ||
                    'Select which athlete fields to show publicly. Personal contact fields (email, phone) are not available.'
                  }}
                </p>
              </div>

              <div class="form-group full-width">
                <label>{{
                  t('registration.publicRegistrantsTabsTitle') ||
                  'Public registrant tabs'
                }}</label>
                <p class="hint compact-hint">
                  {{
                    t('registration.publicRegistrantsTabsHint') ||
                    'Choose whether the public registrations view should show an aggregated All tab and which races or cups visitors can switch between.'
                  }}
                </p>

                <label class="checkbox-label compact-checkbox-label">
                  <input
                    type="checkbox"
                    v-model="publicShowAllTab"
                  />
                  {{
                    t('registration.publicRegistrantsShowAllTab') ||
                    'Show All tab'
                  }}
                </label>

                <div
                  v-if="!hasPersistedFormId"
                  class="public-scope-empty-state"
                >
                  {{
                    t('registration.publicRegistrantsScopesSaveFirst') ||
                    'Save the form first to select race/cup tabs.'
                  }}
                </div>

                <div
                  v-else
                  class="public-scope-selector-grid"
                >
                  <div class="public-scope-card">
                    <div class="public-scope-card-title">
                      {{
                        t('registration.publicRegistrantsRaceTabs') ||
                        'Race tabs'
                      }}
                    </div>
                    <div
                      v-if="availablePublicCompetitions.length === 0"
                      class="public-scope-empty-state"
                    >
                      {{
                        t('registration.publicRegistrantsNoRaces') ||
                        'No races are linked to this form yet.'
                      }}
                    </div>
                    <div v-else class="public-scope-checkbox-list">
                      <label
                        v-for="competition in availablePublicCompetitions"
                        :key="`competition-${competition.id}`"
                        class="checkbox-label compact-checkbox-label"
                      >
                        <input
                          type="checkbox"
                          :checked="isPublicScopeSelected('competition', competition.id)"
                          @change="
                            togglePublicScope(
                              'competition',
                              competition.id,
                              $event.target.checked
                            )
                          "
                        />
                        {{ competition.name }}
                      </label>
                    </div>
                  </div>

                  <div class="public-scope-card">
                    <div class="public-scope-card-title">
                      {{
                        t('registration.publicRegistrantsCupTabs') ||
                        'Cup tabs'
                      }}
                    </div>
                    <div
                      v-if="availablePublicCups.length === 0"
                      class="public-scope-empty-state"
                    >
                      {{
                        t('registration.publicRegistrantsNoCups') ||
                        'No cups are linked to this form yet.'
                      }}
                    </div>
                    <div v-else class="public-scope-checkbox-list">
                      <label
                        v-for="cup in availablePublicCups"
                        :key="`cup-${cup.id}`"
                        class="checkbox-label compact-checkbox-label"
                      >
                        <input
                          type="checkbox"
                          :checked="isPublicScopeSelected('cup', cup.id)"
                          @change="
                            togglePublicScope(
                              'cup',
                              cup.id,
                              $event.target.checked
                            )
                          "
                        />
                        {{ cup.name }}
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  v-if="selectedPublicScopesDetailed.length > 0"
                  class="public-selected-scopes"
                >
                  <div class="public-scope-card-title">
                    {{
                      t('registration.publicRegistrantsTabOrder') ||
                      'Selected tab order'
                    }}
                  </div>
                  <div class="visible-fields">
                    <div
                      v-for="(scope, idx) in selectedPublicScopesDetailed"
                      :key="scope.key"
                      class="visible-field-row public-scope-row"
                    >
                      <span class="drag-index">{{ idx + 1 }}.</span>
                      <div class="public-scope-pill">
                        <span class="public-scope-type">
                          {{ scope.typeLabel }}
                        </span>
                        <span>{{ scope.label }}</span>
                      </div>
                      <span class="public-scope-id">
                        #{{ scope.id }}
                      </span>
                      <div
                        class="row-actions field-actions team-manager-field-actions"
                      >
                        <button
                          type="button"
                          class="btn-icon tm-field-icon-btn"
                          @click="movePublicScope(idx, -1)"
                          :disabled="idx === 0"
                          :title="t('general.moveUp') || 'Move Up'"
                        >
                          <svg
                            class="tm-field-svg"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                          >
                            <path d="M8 3l-4 4h3v6h2V7h3L8 3z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="btn-icon tm-field-icon-btn"
                          @click="movePublicScope(idx, 1)"
                          :disabled="idx === selectedPublicScopesDetailed.length - 1"
                          :title="t('general.moveDown') || 'Move Down'"
                        >
                          <svg
                            class="tm-field-svg"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                          >
                            <path d="M8 13l4-4H9V3H7v6H4l4 4z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="btn-icon tm-field-icon-btn btn-danger"
                          @click="removePublicScope(idx)"
                          :title="t('general.delete') || 'Delete'"
                        >
                          <svg
                            class="tm-field-svg"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                          >
                            <path
                              d="M4.2 3l3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="hint">
                  {{
                    t('registration.publicRegistrantsScopesFallbackHint') ||
                    'If no race or cup tabs are selected, the public registrations view falls back to the aggregated All list.'
                  }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Fields Tab -->
      <div v-if="activeTab === 'fields'" class="tab-content fields-tab">
        <div class="fields-left-panel">
          <FieldsList
            :fields="formData.fields"
            :selected-index="selectedFieldIndex"
            @select="selectField"
            @move="moveField"
            @duplicate="duplicateField"
            @remove="removeField"
            @add="addField"
          />

          <!-- Team Manager Fields Builder (same field types as athletes) -->
          <div
            v-if="formData.settings.multiAthlete"
            class="team-manager-fields-builder"
          >
              <div class="fields-header">
                <span>{{ t('registration.teamManagerFieldsList') }}</span>
                <button
                  type="button"
                  @click="addTeamManagerField"
                  class="btn btn-primary btn-sm"
                >
                  {{ t('registration.addTeamManagerField') }}
                </button>
              </div>

              <div
                v-if="formData.settings.teamManagerFields.length === 0"
                class="empty-team-fields"
              >
                <p>{{ t('registration.noTeamManagerFields') }}</p>
                <button
                  @click="addTeamManagerField"
                  class="btn btn-primary btn-sm"
                >
                  {{ t('registration.addFirstTeamManagerField') }}
                </button>
              </div>

              <div v-else class="team-manager-fields-list">
                <div
                  v-for="(field, index) in formData.settings.teamManagerFields"
                  :key="field.id"
                  class="team-field-item"
                  :class="{ active: selectedTeamManagerFieldIndex === index }"
                  @click="selectTeamManagerField(index)"
                >
                  <div class="field-header">
                    <div class="field-info">
                      <span class="field-label">{{
                        field.label || t('registration.unnamedField')
                      }}</span>
                      <div class="field-meta">
                        <span class="field-type">{{
                          getFieldTypeLabel(field.type)
                        }}</span>
                        <span v-if="field.required" class="required-indicator">{{
                          t('general.required') || 'Required'
                        }}</span>
                      </div>
                    </div>
                    <div class="field-actions team-manager-field-actions">
                      <button
                        type="button"
                        @click.stop="moveTeamManagerField(index, -1)"
                        :disabled="index === 0"
                        class="btn-icon tm-field-icon-btn"
                        :title="t('general.moveUp') || 'Move Up'"
                      >
                        <svg class="tm-field-svg" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M8 3l-4 4h3v6h2V7h3L8 3z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        @click.stop="moveTeamManagerField(index, 1)"
                        :disabled="
                          index ===
                          formData.settings.teamManagerFields.length - 1
                        "
                        class="btn-icon tm-field-icon-btn"
                        :title="t('general.moveDown') || 'Move Down'"
                      >
                        <svg class="tm-field-svg" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M8 13l4-4H9V3H7v6H4l4 4z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        @click.stop="duplicateTeamManagerField(index)"
                        class="btn-icon tm-field-icon-btn"
                        :title="t('general.copy') || 'Duplicate'"
                      >
                        <svg class="tm-field-svg" viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M6 2h7v9H6V2zm-3 3h1v8h7v1H3V5zm2 0v5h5V5H5z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        @click.stop="removeTeamManagerField(index)"
                        class="btn-icon tm-field-icon-btn btn-danger"
                        :title="t('general.delete') || 'Delete'"
                      >
                        <svg class="tm-field-svg" viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M4.2 3l3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Removed inline Team Manager Field Editor; selection now edits on right panel -->
          </div>
        </div>

        <div class="fields-right-panel">
          <FieldEditor
            v-if="editingTargetField"
            :field="editingTargetField"
            :manually-edited="isManuallyEdited"
            :duplicate-names="editorDuplicateNames"
            @typeChange="onEditorTypeChange"
            @sanitizeName="sanitizeEditorName"
            @enableManual="enableEditorManual"
            @disableManual="disableEditorManual"
            @addOption="addEditorOption"
            @moveOption="moveEditorOption"
            @removeOption="removeEditorOption"
          />
        </div>
        <div v-if="false && selectedField" class="field-editor-panel">
          <div class="panel-header">
            <h4>{{ t('registration.editField') }}</h4>
            <span class="field-type-badge">{{
              getFieldTypeLabel(selectedField.type)
            }}</span>
          </div>

          <div class="editor-content">
            <div class="form-group">
              <label>{{ t('registration.fieldType') }} *</label>
              <select
                v-model="selectedField.type"
                @change="onFieldTypeChange"
                class="field-type-select"
              >
                <option value="text">
                  {{ t('registration.fieldTypes.text') }}
                </option>
                <option value="email">
                  {{ t('registration.fieldTypes.email') }}
                </option>
                <option value="name">
                  {{ t('registration.fieldTypes.name') }}
                </option>
                <option value="number">
                  {{ t('registration.fieldTypes.number') }}
                </option>
                <option value="textarea">
                  {{ t('registration.fieldTypes.textarea') }}
                </option>
                <option value="select">
                  {{ t('registration.fieldTypes.select') }}
                </option>
                <option value="radio">
                  {{ t('registration.fieldTypes.radio') }}
                </option>
                <option value="checkbox">
                  {{ t('registration.fieldTypes.checkbox') }}
                </option>
                <option value="date">
                  {{ t('registration.fieldTypes.date') }}
                </option>
                <option value="time">
                  {{ t('registration.fieldTypes.time') }}
                </option>
                <option value="section">
                  {{ t('registration.fieldTypes.section') }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>{{ t('registration.fieldLabel') }} *</label>
                <input
                  v-model="selectedField.label"
                  type="text"
                  required
                  :placeholder="t('registration.fieldLabelPlaceholder')"
                />
              </div>

              <div v-if="selectedField.type !== 'section'" class="form-group">
                <label>{{ t('registration.fieldName') }} *</label>
                <div class="field-name-container">
                  <input
                    v-model="selectedField.name"
                    type="text"
                    required
                    :placeholder="t('registration.fieldNamePlaceholder')"
                    :disabled="!manuallyEditedNames.has(selectedField.id)"
                    @input="sanitizeFieldName"
                    :class="{
                      error: duplicateFieldNames.includes(selectedField.name),
                      'auto-generated': !manuallyEditedNames.has(
                        selectedField.id
                      ),
                    }"
                  />
                  <button
                    v-if="!manuallyEditedNames.has(selectedField.id)"
                    @click="enableManualEdit"
                    class="btn btn-secondary btn-sm edit-name-btn"
                    type="button"
                    title="Enable manual editing of field name"
                  >
                    <span class="btn-icon">✏️</span>
                    Edit
                  </button>
                  <button
                    v-else
                    @click="disableManualEdit"
                    class="btn btn-outline btn-sm edit-name-btn"
                    type="button"
                    title="Reset to auto-generated field name"
                  >
                    <span class="btn-icon">🔄</span>
                    Auto
                  </button>
                </div>
                <small class="field-help">
                  <span v-if="!manuallyEditedNames.has(selectedField.id)">
                    Auto-generated from field label. Click "Edit" to customize.
                  </span>
                  <span v-else>
                    {{ t('registration.fieldNameHelp') }}
                  </span>
                </small>
                <small
                  v-if="duplicateFieldNames.includes(selectedField.name)"
                  class="field-error"
                >
                  ⚠️ This field name is used by another field. Field names must
                  be unique.
                </small>
              </div>
            </div>

            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label>{{ t('registration.fieldPlaceholder') }}</label>
              <input
                v-model="selectedField.placeholder"
                type="text"
                :placeholder="t('registration.fieldPlaceholderPlaceholder')"
              />
            </div>

            <div v-if="selectedField.type === 'section'" class="form-group">
              <label>{{ t('registration.sectionDescription') }}</label>
              <textarea
                v-model="selectedField.description"
                rows="3"
                :placeholder="t('registration.sectionDescriptionPlaceholder')"
              ></textarea>
            </div>

            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="selectedField.required" />
                {{ t('registration.fieldRequired') }}
              </label>
            </div>

            <!-- Options for select, radio, checkbox -->
            <div v-if="hasOptions(selectedField.type)" class="form-group">
              <label>{{ t('registration.fieldOptions') }}</label>
              <div class="options-container">
                <div
                  v-for="(option, optionIndex) in selectedField.options"
                  :key="optionIndex"
                  class="option-item"
                >
                  <input
                    v-model="selectedField.options[optionIndex]"
                    type="text"
                    :placeholder="`Option ${optionIndex + 1}`"
                  />
                  <button
                    @click="removeOption(optionIndex)"
                    class="btn-icon btn-danger"
                  >
                    ✕
                  </button>
                </div>
                <button
                  @click="addOption"
                  class="btn btn-secondary btn-sm add-option-btn"
                >
                  <span class="btn-icon">➕</span>
                  {{ t('registration.addOption') }}
                </button>
              </div>
            </div>

            <!-- Validation rules -->
            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label>{{ t('registration.validation') }}</label>
              <div class="validation-grid">
                <div
                  v-if="
                    selectedField.type === 'text' ||
                    selectedField.type === 'textarea'
                  "
                  class="validation-item"
                >
                  <label>{{ t('registration.minLength') }}</label>
                  <input
                    v-model.number="selectedField.validation.minLength"
                    type="number"
                    min="0"
                    placeholder="Min"
                  />
                </div>
                <div
                  v-if="
                    selectedField.type === 'text' ||
                    selectedField.type === 'textarea'
                  "
                  class="validation-item"
                >
                  <label>{{ t('registration.maxLength') }}</label>
                  <input
                    v-model.number="selectedField.validation.maxLength"
                    type="number"
                    min="0"
                    placeholder="Max"
                  />
                </div>
                <div
                  v-if="selectedField.type === 'number'"
                  class="validation-item"
                >
                  <label>{{ t('registration.minValue') }}</label>
                  <input
                    v-model.number="selectedField.validation.min"
                    type="number"
                    placeholder="Min"
                  />
                </div>
                <div
                  v-if="selectedField.type === 'number'"
                  class="validation-item"
                >
                  <label>{{ t('registration.maxValue') }}</label>
                  <input
                    v-model.number="selectedField.validation.max"
                    type="number"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>

            <!-- Field Preview -->
            <div class="form-group">
              <label>Field Preview</label>
              <div class="field-preview">
                <label class="preview-label">
                  {{ selectedField.label || 'Field Label' }}
                  <span v-if="selectedField.required" class="required">*</span>
                </label>
                <div class="preview-input">
                  <input
                    v-if="
                      [
                        'text',
                        'email',
                        'tel',
                        'number',
                        'date',
                        'time',
                        'url',
                        'password',
                      ].includes(selectedField.type)
                    "
                    :type="selectedField.type"
                    :placeholder="selectedField.placeholder"
                    disabled
                  />
                  <textarea
                    v-else-if="selectedField.type === 'textarea'"
                    :placeholder="selectedField.placeholder"
                    rows="3"
                    disabled
                  ></textarea>
                  <select v-else-if="selectedField.type === 'select'" disabled>
                    <option>
                      {{ selectFieldEmptyLabel(selectedField) }}
                    </option>
                    <option
                      v-for="option in selectedField.options"
                      :key="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                  <div
                    v-else-if="selectedField.type === 'radio'"
                    class="radio-preview"
                  >
                    <div
                      v-if="
                        !selectedField.options ||
                        selectedField.options.length === 0
                      "
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in selectedField.options"
                      :key="option"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :name="'preview-' + selectedField.name"
                        disabled
                      />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <div
                    v-else-if="selectedField.type === 'checkbox'"
                    class="checkbox-preview"
                  >
                    <div
                      v-if="
                        !selectedField.options ||
                        selectedField.options.length === 0
                      "
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in selectedField.options"
                      :key="option"
                      class="checkbox-option"
                    >
                      <input type="checkbox" disabled />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <input
                    v-else-if="selectedField.type === 'file'"
                    type="file"
                    disabled
                  />
                  <div
                    v-else-if="selectedField.type === 'section'"
                    class="section-preview"
                  >
                    <div class="section-divider"></div>
                    <h4 class="section-title">
                      {{ selectedField.label || 'Section Title' }}
                    </h4>
                    <p
                      v-if="selectedField.description"
                      class="section-description"
                    >
                      {{ selectedField.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Translations Tab -->
      <div v-if="activeTab === 'translations'" class="tab-content">
        <div class="content-section basic-info-section translations-section">
          <h4>{{ t('registration.formTranslations') }}</h4>
          <div class="translation-defaults-actions">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="applyTranslationDefaults('ee')"
            >
              {{ t('registration.translationDefaultsEstonian') }}
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="applyTranslationDefaults('en')"
            >
              {{ t('registration.translationDefaultsEnglish') }}
            </button>
          </div>

          <div class="form-grid basic-info-grid translations-grid">
            <div class="translation-group">
              <h5>General & Buttons</h5>
              <div class="form-group">
                <label>{{ t('registration.athleteTitle') }}</label>
                <input
                  v-model="formData.translations.athleteTitle"
                  type="text"
                  :placeholder="t('registration.athleteTitlePlaceholder')"
                />
              </div>
              <div class="form-group">
                <label>{{ t('registration.submitButtonText') }}</label>
                <input
                  v-model="formData.translations.submitButton"
                  type="text"
                  :placeholder="t('registration.submitButtonPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.resetButtonText') }}</label>
                <input
                  v-model="formData.translations.resetButton"
                  type="text"
                  :placeholder="t('registration.resetButtonPlaceholder')"
                />
              </div>
            </div>

            <!-- Date translations removed from form-level; now per-field in FieldEditor -->

            <div class="translation-group">
              <h5>Messages</h5>
              <div class="form-group">
                <label>{{ t('registration.requiredFieldMessage') }}</label>
                <input
                  v-model="formData.translations.requiredFieldMessage"
                  type="text"
                  :placeholder="
                    t('registration.requiredFieldMessagePlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.successMessage') }}</label>
                <textarea
                  v-model="formData.translations.successMessage"
                  rows="2"
                  :placeholder="t('registration.successMessagePlaceholder')"
                ></textarea>
              </div>

              <div class="form-group">
                <label>{{ t('registration.errorMessage') }}</label>
                <textarea
                  v-model="formData.translations.errorMessage"
                  rows="2"
                  :placeholder="t('registration.errorMessagePlaceholder')"
                ></textarea>
              </div>
            </div>

            <div class="translation-group">
              <h5>Competition Flow</h5>
              <div class="form-group">
                <label>{{ t('registration.selectCompetitionLabel') }}</label>
                <input
                  v-model="formData.translations.selectCompetitionLabel"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionLabelPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.selectCompetitionPlaceholder')
                }}</label>
                <input
                  v-model="formData.translations.selectCompetitionPlaceholder"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionPlaceholderText')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.selectCompetitionError') }}</label>
                <input
                  v-model="formData.translations.selectCompetitionError"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionErrorPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.registrationClosed') }}</label>
                <input
                  v-model="formData.translations.registrationClosed"
                  type="text"
                  :placeholder="t('registration.registrationClosedPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.competitionFull') }}</label>
                <input
                  v-model="formData.translations.competitionFull"
                  type="text"
                  :placeholder="t('registration.competitionFullPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.spotsLeft') }}</label>
                <input
                  v-model="formData.translations.spotsLeft"
                  type="text"
                  :placeholder="t('registration.spotsLeftPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.deadline') }}</label>
                <input
                  v-model="formData.translations.deadline"
                  type="text"
                  :placeholder="t('registration.deadlinePlaceholder')"
                />
              </div>
            </div>

            <div class="translation-group">
              <h5>
                {{
                  t('registration.publicRegistrationsSectionTitle') ||
                  'Public Registrations'
                }}
              </h5>
              <div class="form-group">
                <label>{{
                  t('registration.publicRegisterButtonLabel') ||
                  'Register Button'
                }}</label>
                <input
                  v-model="formData.translations.toggleRegister"
                  type="text"
                  :placeholder="t('registration.publicRegisterButton')"
                />
              </div>
              <div class="form-group">
                <label>{{
                  t('registration.publicRegistrationsButtonLabel') ||
                  'Registrations Button'
                }}</label>
                <input
                  v-model="formData.translations.toggleRegistrations"
                  type="text"
                  :placeholder="t('nav.registrations')"
                />
              </div>
              <div class="form-group">
                <label>{{
                  t('registration.publicRefreshButtonLabel') ||
                  'Refresh Button'
                }}</label>
                <input
                  v-model="formData.translations.refreshButton"
                  type="text"
                  :placeholder="t('registration.publicRefreshButton')"
                />
              </div>
              <div class="form-group">
                <label>{{
                  t('registration.publicAllTabLabel') || 'All Tab Label'
                }}</label>
                <input
                  v-model="formData.translations.publicAllTab"
                  type="text"
                  :placeholder="t('registration.publicAllTab')"
                />
              </div>
              <div class="form-group">
                <label>{{
                  t('registration.publicEmptyRegistrationsMessageLabel') ||
                  'Empty Registrations Message'
                }}</label>
                <input
                  v-model="formData.translations.publicNoRegistrationsYet"
                  type="text"
                  :placeholder="t('registration.publicNoRegistrationsYet')"
                />
              </div>
            </div>

            <!-- Multi-Athlete Translations (only show if multi-athlete is enabled) -->
            <div
              v-if="formData.settings.multiAthlete"
              class="translation-group"
            >
              <h5>{{ t('registration.multiAthleteTranslations') }}</h5>

              <div class="form-group">
                <label>{{ t('registration.addAthleteButton') }}</label>
                <input
                  v-model="formData.translations.addAthleteButton"
                  type="text"
                  :placeholder="t('registration.addAthleteButtonPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.removeAthleteButton') }}</label>
                <input
                  v-model="formData.translations.removeAthleteButton"
                  type="text"
                  :placeholder="
                    t('registration.removeAthleteButtonPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.teamManagerContactTitle') }}</label>
                <input
                  v-model="formData.translations.teamManagerContactTitle"
                  type="text"
                  :placeholder="
                    t('registration.teamManagerContactTitlePlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.teamManagerSectionDescription')
                }}</label>
                <textarea
                  v-model="formData.translations.teamManagerSectionDescription"
                  rows="2"
                  :placeholder="
                    t('registration.teamManagerSectionDescriptionPlaceholder')
                  "
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Email Tab -->
      <div v-if="activeTab === 'email'" class="tab-content">
        <div class="content-section basic-info-section email-settings-section">
          <h4>
            {{ t('registration.emailSettings') || 'Email Notifications' }}
          </h4>

          <div class="form-grid email-settings-grid">
            <div class="form-group full-width">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.email.enabled"
                />
                {{
                  t('registration.enableEmailOnSubmit') ||
                  'Send confirmation email on submit'
                }}
              </label>
            </div>

            <div class="form-group full-width smtp-settings-card">
              <h5 class="email-subsection-title">
                {{ t('registration.smtpSettingsTitle') || 'SMTP Settings' }}
              </h5>
              <div class="smtp-compact-grid">
                <div class="form-group">
                  <label>{{ t('registration.smtpHost') || 'SMTP Host' }}</label>
                  <input
                    v-model="formData.settings.email.smtp.host"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="smtp.example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.smtpPort') || 'SMTP Port' }}</label>
                  <input
                    v-model.number="formData.settings.email.smtp.port"
                    type="number"
                    min="1"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="587"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.smtpUser') || 'SMTP User' }}</label>
                  <input
                    v-model="formData.settings.email.smtp.user"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="no-reply@example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{
                    t('registration.smtpPass') || 'SMTP Password'
                  }}</label>
                  <input
                    v-model="formData.settings.email.smtp.pass"
                    type="password"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="••••••••"
                    autocomplete="new-password"
                  />
                </div>

                <div class="form-group full-span">
                  <label class="checkbox-label compact-checkbox-label">
                    <input
                      type="checkbox"
                      v-model="formData.settings.email.smtp.secure"
                      :disabled="!formData.settings.email.enabled"
                    />
                    {{ t('registration.smtpSecure') || 'Use SSL/TLS (secure)' }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Test Connection Button -->
            <div class="form-group full-width">
              <button
                @click="testEmailConnection"
                :disabled="
                  !formData.settings.email.enabled ||
                  !isEmailConfigValid ||
                  isTestingConnection
                "
                class="btn btn-secondary"
                type="button"
              >
                <span v-if="isTestingConnection" class="btn-icon">⏳</span>
                <span v-else class="btn-icon">🔗</span>
                {{
                  isTestingConnection
                    ? t('registration.testingConnection') || 'Testing...'
                    : t('registration.testConnection') || 'Test Connection'
                }}
              </button>
              <p class="help-text">
                {{
                  t('registration.testConnectionHelp') ||
                  'Test your SMTP settings before saving. This will attempt to connect to your mail server.'
                }}
              </p>
              <div
                v-if="connectionTestResult"
                class="connection-result"
                :class="
                  connectionTestResult.success
                    ? 'connection-success'
                    : 'connection-error'
                "
              >
                <strong class="connection-result-title">
                  {{
                    connectionTestResult.success
                      ? t('general.success') || 'Success'
                      : t('general.error') || 'Error'
                  }}
                </strong>
                <span class="connection-result-message">
                  {{ connectionTestResult.message }}
                </span>
              </div>
            </div>

            <div class="form-group full-width sender-settings-card">
              <h5 class="email-subsection-title">
                {{ t('registration.senderSettingsTitle') || 'Sender Settings' }}
              </h5>
              <div class="sender-compact-grid">
                <div class="form-group">
                  <label>{{
                    t('registration.fromEmail') || 'From Email'
                  }}</label>
                  <input
                    v-model="formData.settings.email.fromEmail"
                    type="email"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="no-reply@example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.fromName') || 'From Name' }}</label>
                  <input
                    v-model="formData.settings.email.fromName"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    :placeholder="formData.title || 'Registration'"
                  />
                </div>
              </div>
            </div>

            <div
              class="form-group full-width athlete-template-card"
              v-if="emailFieldOptions.length > 0"
            >
              <h5 class="email-subsection-title">
                {{
                  t('registration.athleteEmailTemplateTitle') ||
                  'Athlete Email Template'
                }}
              </h5>

              <div class="form-group">
                <label>{{
                  t('registration.recipientField') || 'Recipient field'
                }}</label>
                <select
                  v-model="formData.settings.email.recipientField"
                  :disabled="!formData.settings.email.enabled"
                >
                  <option
                    v-for="opt in emailFieldOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }} ({{ opt.value }})
                  </option>
                </select>
                <p class="help-text" v-if="emailFieldOptions.length > 1">
                  {{
                    t('registration.recipientFieldHint') ||
                    'Choose which email field the confirmation will be sent to.'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.emailSubject') || 'Email Subject'
                }}</label>
                <input
                  v-model="formData.settings.email.subject"
                  type="text"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.emailSubjectPlaceholder') ||
                    'Registration Confirmation'
                  "
                />
                <p class="help-text">
                  {{
                    t('registration.emailSubjectHelp') ||
                    'Subject line for the confirmation email sent to participants'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.emailMessage') || 'Email Message'
                }}</label>
                <textarea
                  v-model="formData.settings.email.message"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.emailMessagePlaceholder') ||
                    'Thank you for your registration...'
                  "
                  rows="6"
                  class="form-textarea"
                ></textarea>
                <p class="help-text">
                  {{
                    t('registration.emailMessageHelp') ||
                    'Custom message to send to participants. You can use placeholders:'
                  }}
                </p>

                <div class="placeholder-legend">
                  <h5>
                    {{
                      t('registration.availablePlaceholders') ||
                      'Available Placeholders'
                    }}
                  </h5>
                  <div class="placeholder-grid">
                    <div class="placeholder-item">
                      <code>{participantName}</code>
                      <span>{{
                        t('registration.placeholderParticipantName') ||
                        "Participant's name"
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{competitionName}</code>
                      <span>{{
                        t('registration.placeholderCompetitionName') ||
                        'Competition name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{cupName}</code>
                      <span>{{
                        t('registration.placeholderCupName') ||
                        'Cup/Season name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{formTitle}</code>
                      <span>{{
                        t('registration.placeholderFormTitle') || 'Form title'
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="form-group full-width manager-template-card"
              v-if="
                formData.settings.multiAthlete &&
                managerEmailFieldOptions.length > 0
              "
            >
              <h5 class="email-subsection-title">
                {{
                  t('registration.managerEmailTemplateTitle') ||
                  'Manager Email Template'
                }}
              </h5>

              <div class="form-group">
                <label>{{
                  t('registration.managerRecipientField') ||
                  'Manager recipient field'
                }}</label>
                <select
                  v-model="formData.settings.email.managerRecipientField"
                  :disabled="!formData.settings.email.enabled"
                >
                  <option value="">
                    {{ t('registration.noneOption') || 'None' }}
                  </option>
                  <option
                    v-for="opt in managerEmailFieldOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }} ({{ opt.value }})
                  </option>
                </select>
                <p class="help-text">
                  {{
                    t('registration.managerRecipientFieldHint') ||
                    'Choose the team manager email field for the summary email.'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.managerEmailSubject') ||
                  'Manager Email Subject'
                }}</label>
                <input
                  v-model="formData.settings.email.managerSubject"
                  type="text"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.managerEmailSubjectPlaceholder') ||
                    'Registration Summary'
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.managerEmailMessage') ||
                  'Manager Email Message'
                }}</label>
                <textarea
                  v-model="formData.settings.email.managerMessage"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.managerEmailMessagePlaceholder') ||
                    'Summary for {participantName}...'
                  "
                  rows="8"
                  class="form-textarea"
                ></textarea>
                <p class="help-text">
                  {{
                    t('registration.managerEmailMessageHelp') ||
                    'Custom summary message for team managers. You can use placeholders:'
                  }}
                </p>
                <div class="placeholder-legend">
                  <h5>
                    {{
                      t('registration.availablePlaceholders') ||
                      'Available Placeholders'
                    }}
                  </h5>
                  <div class="placeholder-grid">
                    <div class="placeholder-item">
                      <code>{participantName}</code>
                      <span>{{
                        t('registration.placeholderParticipantNameList') ||
                        'All participant names (comma-separated for multi-athlete)'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{competitionName}</code>
                      <span>{{
                        t('registration.placeholderCompetitionName') ||
                        'Competition name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{cupName}</code>
                      <span>{{
                        t('registration.placeholderCupName') ||
                        'Cup/Season name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{formTitle}</code>
                      <span>{{
                        t('registration.placeholderFormTitle') || 'Form title'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{managerName}</code>
                      <span>{{
                        t('registration.placeholderManagerName') ||
                        "Manager's name"
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{registrationsText}</code>
                      <span>{{
                        t('registration.placeholderRegistrationsText') ||
                        'Formatted registration summary text'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{registrationsJson}</code>
                      <span>{{
                        t('registration.placeholderRegistrationsJson') ||
                        'Raw registrations data as JSON'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{athleteCount}</code>
                      <span>{{
                        t('registration.placeholderAthleteCount') ||
                        'Number of athletes in the submission'
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Tab -->
      <div v-if="activeTab === 'preview'" class="tab-content">
        <div class="preview-container content-section basic-info-section">
          <h4>{{ t('registration.formPreview') }}</h4>
          <div class="form-preview">
            <div class="preview-header">
              <h3>{{ formData.title || t('registration.untitledForm') }}</h3>
              <p v-if="formData.description">{{ formData.description }}</p>
            </div>

            <div class="preview-fields">
              <div
                v-if="formData.fields.length === 0"
                class="no-fields-preview"
              >
                <p>
                  No fields added yet. Go to the Fields tab to add form fields.
                </p>
              </div>
              <div
                v-for="field in formData.fields"
                :key="field.id"
                class="preview-field"
              >
                <label class="preview-label">
                  {{ field.label }}
                  <span v-if="field.required" class="required">*</span>
                </label>
                <div class="preview-input">
                  <input
                    v-if="
                      [
                        'text',
                        'email',
                        'tel',
                        'number',
                        'date',
                        'time',
                        'url',
                        'password',
                      ].includes(field.type)
                    "
                    :type="field.type"
                    :placeholder="field.placeholder"
                    disabled
                  />
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    :placeholder="field.placeholder"
                    rows="3"
                    disabled
                  ></textarea>
                  <select v-else-if="field.type === 'select'" disabled>
                    <option>
                      {{ selectFieldEmptyLabel(field) }}
                    </option>
                    <option v-for="option in field.options" :key="option">
                      {{ option }}
                    </option>
                  </select>
                  <div v-else-if="field.type === 'radio'" class="radio-preview">
                    <div
                      v-if="!field.options || field.options.length === 0"
                      class="no-options-message"
                    >
                      No options configured for this field.
                    </div>
                    <div
                      v-for="option in field.options"
                      :key="option"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :name="'preview-' + field.name"
                        disabled
                      />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <div
                    v-else-if="field.type === 'checkbox'"
                    class="checkbox-preview"
                  >
                    <div
                      v-if="!field.options || field.options.length === 0"
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in field.options"
                      :key="option"
                      class="checkbox-option"
                    >
                      <input type="checkbox" disabled />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <input
                    v-else-if="field.type === 'file'"
                    type="file"
                    disabled
                  />
                  <div
                    v-else-if="field.type === 'section'"
                    class="section-preview"
                  >
                    <div class="section-divider"></div>
                    <h4 class="section-title">
                      {{ field.label || 'Section Title' }}
                    </h4>
                    <p v-if="field.description" class="section-description">
                      {{ field.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="preview-actions">
              <button class="preview-submit-btn" disabled>
                {{ formData.translations.submitButton || 'Submit' }}
              </button>
              <button class="preview-reset-btn" disabled>
                {{ formData.translations.resetButton || 'Reset' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="form-builder-footer">
      <button
        type="button"
        @click="$emit('close')"
        class="btn btn-footer-cancel"
      >
        {{ t('button.cancel') }}
      </button>
      <button
        type="button"
        @click="saveForm"
        :disabled="!isFormValid"
        class="btn btn-footer-save"
      >
        {{ t('button.save') }}
      </button>
    </footer>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import enMessages from '@/locales/en.json'
import eeMessages from '@/locales/ee.json'
import FieldsList from './FieldsList.vue'
import FieldEditor from './FieldEditor.vue'

/** Form-level translation strings from locale files (registration.* + nav.registrations). */
function buildFormTranslationDefaults(messages) {
  const r = messages.registration
  const nav = messages.nav
  return {
    athleteTitle: r.athleteTitlePlaceholder,
    submitButton: r.submitButtonPlaceholder,
    resetButton: r.resetButtonPlaceholder,
    requiredFieldMessage: r.requiredFieldMessagePlaceholder,
    successMessage: r.successMessagePlaceholder,
    errorMessage: r.errorMessagePlaceholder,
    selectCompetitionLabel: r.selectCompetitionLabelPlaceholder,
    selectCompetitionPlaceholder: r.selectCompetitionPlaceholderText,
    selectCompetitionError: r.selectCompetitionErrorPlaceholder,
    selectEmptyOptionPlaceholder: r.selectOption,
    registrationClosed: r.registrationClosedPlaceholder,
    competitionFull: r.competitionFullPlaceholder,
    spotsLeft: r.spotsLeftPlaceholder,
    deadline: r.deadlinePlaceholder,
    addAthleteButton: r.addAthleteButtonPlaceholder,
    removeAthleteButton: r.removeAthleteButtonPlaceholder,
    teamManagerContactTitle: r.teamManagerContactTitlePlaceholder,
    teamManagerSectionDescription: r.teamManagerSectionDescriptionPlaceholder,
    publicAllTab: r.publicAllTab,
    publicNoRegistrationsYet: r.publicNoRegistrationsYet,
    toggleRegister: r.publicRegisterButton,
    toggleRegistrations: nav.registrations,
    refreshButton: r.publicRefreshButton,
  }
}

export default {
  name: 'FormBuilder',
  components: {
    FieldsList,
    FieldEditor,
  },
  props: {
    form: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const PUBLIC_SCOPE_TYPES = new Set(['competition', 'cup'])
    const createDefaultPublicRegistrantsSettings = () => ({
      enabled: false,
      visibleFieldKeys: [],
      columnLabels: {},
      showAllTab: true,
      visibleScopes: [],
    })
    const normalizePublicScope = (scope) => {
      if (!scope || typeof scope !== 'object') return null
      const type = String(scope.type || '').toLowerCase()
      const id = parseInt(scope.id)
      if (!PUBLIC_SCOPE_TYPES.has(type) || Number.isNaN(id)) return null
      return { type, id }
    }
    const normalizePublicScopes = (scopes) => {
      const seen = new Set()
      return (Array.isArray(scopes) ? scopes : []).reduce((acc, scope) => {
        const normalized = normalizePublicScope(scope)
        if (!normalized) return acc
        const key = `${normalized.type}:${normalized.id}`
        if (seen.has(key)) return acc
        seen.add(key)
        acc.push(normalized)
        return acc
      }, [])
    }
    const ensurePublicRegistrantsSettings = (value = {}) => {
      const normalized = {
        ...createDefaultPublicRegistrantsSettings(),
        ...(value || {}),
      }
      normalized.visibleFieldKeys = Array.isArray(normalized.visibleFieldKeys)
        ? [...normalized.visibleFieldKeys]
        : []
      normalized.columnLabels =
        normalized.columnLabels && typeof normalized.columnLabels === 'object'
          ? { ...normalized.columnLabels }
          : {}
      normalized.showAllTab = normalized.showAllTab !== false
      normalized.visibleScopes = normalizePublicScopes(normalized.visibleScopes)
      return normalized
    }
    const availablePublicCompetitions = ref([])
    const availablePublicCups = ref([])
    const publicScopeOptionsLoaded = ref(false)
    const hasPersistedFormId = computed(() => !!(props.form && props.form.id))
    // Public registrants helpers
    const publicVisibleKeys = computed({
      get: () => formData.value.settings.publicRegistrants.visibleFieldKeys,
      set: (v) =>
        (formData.value.settings.publicRegistrants.visibleFieldKeys = v),
    })
    const publicColumnLabels = computed({
      get: () => formData.value.settings.publicRegistrants.columnLabels || {},
      set: (v) =>
        (formData.value.settings.publicRegistrants.columnLabels = v || {}),
    })
    const publicShowAllTab = computed({
      get: () => formData.value.settings.publicRegistrants.showAllTab !== false,
      set: (value) =>
        (formData.value.settings.publicRegistrants.showAllTab = !!value),
    })
    const publicVisibleScopes = computed({
      get: () =>
        normalizePublicScopes(
          formData.value.settings.publicRegistrants.visibleScopes
        ),
      set: (value) =>
        (formData.value.settings.publicRegistrants.visibleScopes =
          normalizePublicScopes(value)),
    })
    const publicFieldOptions = computed(() => {
      // Athlete + team manager fields; exclude obvious PII types
      const disallowTypes = new Set(['email', 'tel', 'phone'])
      const athleteFields = (formData.value.fields || [])
        .filter(
          (f) =>
            f &&
            f.name &&
            f.type &&
            !disallowTypes.has(String(f.type).toLowerCase())
        )
        .map((f) => ({ value: f.name, label: f.label || f.name }))

      const teamManagerFields = formData.value.settings?.multiAthlete
        ? (formData.value.settings.teamManagerFields || [])
        : []

      const managerOptions = teamManagerFields
        .filter(
          (f) =>
            f &&
            f.name &&
            f.type &&
            !disallowTypes.has(String(f.type).toLowerCase())
        )
        .map((f) => ({
          value: `teamManager.${f.name}`,
          label: `Team Manager: ${f.label || f.name}`,
        }))

      return [...athleteFields, ...managerOptions]
    })
    const isKeySelectedElsewhere = (key, exceptIdx) => {
      return publicVisibleKeys.value.some(
        (k, i) => k === key && i !== exceptIdx
      )
    }
    const addPublicKey = () => {
      const first = publicFieldOptions.value.find(
        (opt) => !publicVisibleKeys.value.includes(opt.value)
      )
      if (first)
        publicVisibleKeys.value = [...publicVisibleKeys.value, first.value]
    }
    const removePublicKey = (idx) => {
      const next = [...publicVisibleKeys.value]
      const removed = next.splice(idx, 1)[0]
      publicVisibleKeys.value = next
      if (removed && publicColumnLabels.value[removed]) {
        const labels = { ...publicColumnLabels.value }
        delete labels[removed]
        publicColumnLabels.value = labels
      }
    }
    const movePublicKey = (idx, dir) => {
      const next = [...publicVisibleKeys.value]
      const ni = idx + dir
      if (ni < 0 || ni >= next.length) return
      const [item] = next.splice(idx, 1)
      next.splice(ni, 0, item)
      publicVisibleKeys.value = next
    }
    const onColumnLabelChange = (key, value) => {
      const labels = { ...publicColumnLabels.value }
      if (value && value.trim()) labels[key] = value
      else delete labels[key]
      publicColumnLabels.value = labels
    }
    const getPublicScopeKey = (type, id) => `${type}:${id}`
    const availablePublicScopes = computed(() => [
      ...availablePublicCompetitions.value.map((competition) => ({
        key: getPublicScopeKey('competition', competition.id),
        type: 'competition',
        id: competition.id,
        label: competition.name || `Race ${competition.id}`,
        typeLabel:
          t('registration.publicRegistrantsRaceScopeLabel') || 'Race',
      })),
      ...availablePublicCups.value.map((cup) => ({
        key: getPublicScopeKey('cup', cup.id),
        type: 'cup',
        id: cup.id,
        label: cup.name || `Cup ${cup.id}`,
        typeLabel: t('registration.publicRegistrantsCupScopeLabel') || 'Cup',
      })),
    ])
    const publicScopeLookup = computed(() => {
      return availablePublicScopes.value.reduce((acc, scope) => {
        acc[scope.key] = scope
        return acc
      }, {})
    })
    const selectedPublicScopesDetailed = computed(() => {
      return publicVisibleScopes.value.map((scope) => {
        const key = getPublicScopeKey(scope.type, scope.id)
        const match = publicScopeLookup.value[key]
        return (
          match || {
            key,
            type: scope.type,
            id: scope.id,
            label: `${scope.type} #${scope.id}`,
            typeLabel:
              scope.type === 'cup'
                ? t('registration.publicRegistrantsCupScopeLabel') || 'Cup'
                : t('registration.publicRegistrantsRaceScopeLabel') || 'Race',
          }
        )
      })
    })
    const isPublicScopeSelected = (type, id) => {
      return publicVisibleScopes.value.some(
        (scope) => scope.type === type && scope.id === id
      )
    }
    const togglePublicScope = (type, id, enabled) => {
      if (!enabled) {
        publicVisibleScopes.value = publicVisibleScopes.value.filter(
          (scope) => !(scope.type === type && scope.id === id)
        )
        return
      }
      if (isPublicScopeSelected(type, id)) return
      publicVisibleScopes.value = [
        ...publicVisibleScopes.value,
        { type, id: parseInt(id) },
      ]
    }
    const removePublicScope = (idx) => {
      const next = [...publicVisibleScopes.value]
      next.splice(idx, 1)
      publicVisibleScopes.value = next
    }
    const movePublicScope = (idx, dir) => {
      const next = [...publicVisibleScopes.value]
      const nextIndex = idx + dir
      if (nextIndex < 0 || nextIndex >= next.length) return
      const [scope] = next.splice(idx, 1)
      next.splice(nextIndex, 0, scope)
      publicVisibleScopes.value = next
    }
    const loadPublicScopeOptions = async () => {
      publicScopeOptionsLoaded.value = false
      if (!props.form || !props.form.id) {
        availablePublicCompetitions.value = []
        availablePublicCups.value = []
        publicScopeOptionsLoaded.value = true
        return
      }

      try {
        const [competitionResponse, cupResponse] = await Promise.all([
          axios.get('/registration/competitions'),
          axios.get('/registration/cups'),
        ])
        const formId = Number(props.form.id)
        availablePublicCompetitions.value = Array.isArray(
          competitionResponse.data
        )
          ? competitionResponse.data.filter(
              (competition) => Number(competition.registration_form_id) === formId
            )
          : []
        availablePublicCups.value = Array.isArray(cupResponse.data)
          ? cupResponse.data.filter(
              (cup) => Number(cup.registration_form_id) === formId
            )
          : []
      } catch (error) {
        console.error('Error loading public registrant scopes:', error)
        availablePublicCompetitions.value = []
        availablePublicCups.value = []
      } finally {
        publicScopeOptionsLoaded.value = true
      }
    }

    // Active tab
    const activeTab = ref('basic')

    // Form data

    const formData = ref({
      name: '',
      title: '',
      description: '',
      isActive: true,
      fields: [],
      settings: {
        multiAthlete: false,
        teamManagerFields: [],
        email: {
          enabled: false,
          smtp: { host: '', port: 587, secure: false, user: '', pass: '' },
          fromEmail: '',
          fromName: '',
          recipientField: '',
          managerRecipientField: '',
          subject: 'Registration Confirmation',
          message:
            'Thank you for your registration! Your submission has been received and is being processed.',
          managerSubject: 'Registration Summary',
          managerMessage: '',
        },
        publicRegistrants: {
          ...createDefaultPublicRegistrantsSettings(),
        },
      },
      translations: {
        ...buildFormTranslationDefaults(eeMessages),
        teamManagerSectionDescription: '',
      },
    })

    const applyTranslationDefaults = (localeCode) => {
      const messages = localeCode === 'ee' ? eeMessages : enMessages
      const defaults = buildFormTranslationDefaults(messages)
      formData.value.translations = {
        ...formData.value.translations,
        ...defaults,
      }
    }

    // Field management
    const selectedFieldIndex = ref(-1)
    const fieldIdCounter = ref(1)
    // Email recipient options from form fields
    const emailFieldOptions = computed(() => {
      return (formData.value.fields || [])
        .filter((f) => f && f.type === 'email' && f.name)
        .map((f) => ({ value: f.name, label: f.label || f.name }))
    })
    const managerEmailFieldOptions = computed(() => {
      return (formData.value.settings.teamManagerFields || [])
        .filter((f) => f && f.type === 'email' && f.name)
        .map((f) => ({ value: f.name, label: f.label || f.name }))
    })

    // Check if email configuration is valid
    const isEmailConfigValid = computed(() => {
      if (!formData.value.settings.email.enabled) return false
      const email = formData.value.settings.email
      return (
        email.smtp.host &&
        email.smtp.port &&
        email.smtp.user &&
        email.smtp.pass &&
        email.fromEmail &&
        (email.recipientField ||
          (formData.value.settings.multiAthlete && email.managerRecipientField))
      )
    })

    // Test connection state
    const isTestingConnection = ref(false)
    const connectionTestResult = ref(null)

    // Test email connection
    const testEmailConnection = async () => {
      if (!isEmailConfigValid.value) return

      isTestingConnection.value = true
      connectionTestResult.value = null

      try {
        const response = await axios.post('/registration/test-email', {
          smtp: formData.value.settings.email.smtp,
          fromEmail: formData.value.settings.email.fromEmail,
          fromName: formData.value.settings.email.fromName,
        })

        const result = response.data

        if (response.status === 200) {
          connectionTestResult.value = {
            success: true,
            message:
              'Connection successful! SMTP settings are working correctly.',
          }
        } else {
          connectionTestResult.value = {
            success: false,
            message:
              result.error ||
              'Connection failed. Please check your SMTP settings.',
          }
        }
      } catch (error) {
        console.error('Test connection error:', error)
        connectionTestResult.value = {
          success: false,
          message:
            'Connection test failed. Please check your network and try again.',
        }
      } finally {
        isTestingConnection.value = false
      }
    }

    // Keep recipientField valid as fields change
    watch(
      () => formData.value.fields,
      () => {
        const opts = emailFieldOptions.value
        const current = formData.value.settings.email.recipientField
        if (!opts.find((o) => o.value === current)) {
          formData.value.settings.email.recipientField = opts[0]
            ? opts[0].value
            : ''
        }
      },
      { deep: true, immediate: true }
    )
    watch(
      () => formData.value.settings.teamManagerFields,
      () => {
        const opts = managerEmailFieldOptions.value
        const current = formData.value.settings.email.managerRecipientField
        if (!opts.find((o) => o.value === current)) {
          formData.value.settings.email.managerRecipientField = opts[0]
            ? opts[0].value
            : ''
        }
      },
      { deep: true, immediate: true }
    )

    // Team Manager Field management
    const selectedTeamManagerFieldIndex = ref(-1)
    const teamManagerFieldIdCounter = ref(1)

    // When editing Team Manager fields in the same FieldEditor on the right,
    // we expose a computed target field that resolves to either the selected
    // athlete field or the selected team manager field.
    const editingTargetField = computed(() => {
      if (selectedTeamManagerFieldIndex.value >= 0) {
        return (
          formData.value.settings.teamManagerFields[
            selectedTeamManagerFieldIndex.value
          ] || null
        )
      }
      return selectedField.value
    })

    // Plumb editor bindings depending on which target is selected
    const isEditingTeamManager = computed(
      () => selectedTeamManagerFieldIndex.value >= 0
    )
    const isManuallyEdited = computed(() => {
      if (isEditingTeamManager.value) return false
      return (
        !!selectedField.value &&
        manuallyEditedNames.value.has(selectedField.value.id)
      )
    })
    const editorDuplicateNames = computed(() => {
      // Duplicate names validation applies only to athlete fields list
      return duplicateFieldNames.value
    })
    const onEditorTypeChange = () => {
      if (isEditingTeamManager.value) {
        onTeamManagerFieldTypeChange()
      } else {
        onFieldTypeChange()
      }
    }
    const sanitizeEditorName = () => {
      if (isEditingTeamManager.value) {
        sanitizeTeamManagerFieldName()
      } else {
        sanitizeFieldName()
      }
    }
    const enableEditorManual = () => {
      if (!isEditingTeamManager.value) enableManualEdit()
    }
    const disableEditorManual = () => {
      if (!isEditingTeamManager.value) disableManualEdit()
    }
    const addEditorOption = () => {
      if (isEditingTeamManager.value) addTeamManagerFieldOption()
      else addOption()
    }
    const moveEditorOption = (idx, direction) => {
      if (isEditingTeamManager.value) moveTeamManagerFieldOption(idx, direction)
      else moveOption(idx, direction)
    }
    const removeEditorOption = (idx) => {
      if (isEditingTeamManager.value) removeTeamManagerFieldOption(idx)
      else removeOption(idx)
    }

    // Computed properties
    const isEditing = computed(() => !!props.form)
    const selectedField = computed(() => {
      return selectedFieldIndex.value >= 0
        ? formData.value.fields[selectedFieldIndex.value]
        : null
    })

    const selectedTeamManagerField = computed(() => {
      return selectedTeamManagerFieldIndex.value >= 0
        ? formData.value.settings.teamManagerFields[
            selectedTeamManagerFieldIndex.value
          ]
        : null
    })

    // Check for duplicate field names
    const duplicateFieldNames = computed(() => {
      const names = formData.value.fields
        .map((field) => field.name)
        .filter((name) => name && name.trim() !== '')

      const duplicates = names.filter(
        (name, index) => names.indexOf(name) !== index
      )
      return [...new Set(duplicates)]
    })

    const isFormValid = computed(() => {
      return (
        formData.value.name.trim() &&
        formData.value.title.trim() &&
        formData.value.fields.length > 0 &&
        formData.value.fields.every((field) => {
          // Section fields only need a label and type
          if (field.type === 'section') {
            return field.label && field.type
          }
          // Other fields need label, name, and type
          return field.label && field.name && field.type
        }) &&
        duplicateFieldNames.value.length === 0
      )
    })

    // Initialize form data
    const deepClone = (v) => {
      try {
        return JSON.parse(JSON.stringify(v))
      } catch {
        return v
      }
    }

    const initializeForm = () => {
      if (props.form) {
        formData.value = {
          ...formData.value, // Keep defaults
          ...deepClone(props.form),
          // Ensure isActive is properly converted to boolean
          isActive: Boolean(props.form.isActive),
          fields: Array.isArray(props.form.fields)
            ? deepClone(props.form.fields)
            : [],

          settings: {
            ...formData.value.settings,
            ...(props.form.settings || {}),
            teamManagerFields: Array.isArray(
              props.form.settings && props.form.settings.teamManagerFields
            )
              ? deepClone(props.form.settings.teamManagerFields)
              : Array.isArray(props.form.teamManagerFields)
              ? deepClone(props.form.teamManagerFields)
              : [],
            email: {
              enabled: !!(
                props.form.settings &&
                props.form.settings.email &&
                props.form.settings.email.enabled
              ),
              smtp: {
                host:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.host) ||
                  '',
                port:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.port) ||
                  587,
                secure: !!(
                  props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.smtp &&
                  props.form.settings.email.smtp.secure
                ),
                user:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.user) ||
                  '',
                pass:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.pass) ||
                  '',
              },
              fromEmail:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.fromEmail) ||
                '',
              fromName:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.fromName) ||
                '',
              recipientField:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.recipientField) ||
                '',
              managerRecipientField:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerRecipientField) ||
                '',
              subject:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.subject) ||
                'Registration Confirmation',
              message:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.message) ||
                'Thank you for your registration! Your submission has been received and is being processed.',
              managerSubject:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerSubject) ||
                'Registration Summary',
              managerMessage:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerMessage) ||
                '',
            },
            publicRegistrants: ensurePublicRegistrantsSettings(
              props.form.settings && props.form.settings.publicRegistrants
                ? deepClone(props.form.settings.publicRegistrants)
                : undefined
            ),
          },
          translations: {
            ...formData.value.translations,
            ...(props.form.translations || {}),
          },
        }

        if (!props.form.id) {
          formData.value.settings.publicRegistrants.visibleScopes = []
          formData.value.settings.publicRegistrants.showAllTab = true
        }

        // Date labels are managed per-field; no form-level month names handling
        // No-op for public name labels. Name/date labels are now configured per field.
        // Set counter based on existing fields
        const maxId = Math.max(
          ...formData.value.fields.map((f) => parseInt(f.id) || 0),
          0
        )
        fieldIdCounter.value = maxId + 1

        // Set counter based on existing team manager fields
        const maxTeamManagerId = Math.max(
          ...formData.value.settings.teamManagerFields.map(
            (f) => parseInt(f.id) || 0
          ),
          0
        )
        teamManagerFieldIdCounter.value = maxTeamManagerId + 1
      }
    }

    // Field operations
    const addField = () => {
      const newField = {
        id: `field_${fieldIdCounter.value++}`,
        type: 'text',
        label: '',
        name: '',
        placeholder: '',
        required: false,
        options: [],
        validation: {},
      }
      formData.value.fields.push(newField)
      selectedFieldIndex.value = formData.value.fields.length - 1
      // Don't mark new fields as manually edited
      manuallyEditedNames.value.delete(newField.id)
    }

    const removeField = (index) => {
      if (confirm(t('registration.deleteFieldConfirm'))) {
        formData.value.fields.splice(index, 1)
        if (selectedFieldIndex.value >= formData.value.fields.length) {
          selectedFieldIndex.value = formData.value.fields.length - 1
        }
      }
    }

    const duplicateField = (index) => {
      const field = formData.value.fields[index]
      const duplicated = {
        ...field,
        id: `field_${fieldIdCounter.value++}`,
        name: `${field.name}_copy`,
        label: `${field.label} (Copy)`,
      }
      formData.value.fields.splice(index + 1, 0, duplicated)
      selectedFieldIndex.value = index + 1
      // Don't mark duplicated fields as manually edited
      manuallyEditedNames.value.delete(duplicated.id)
    }

    const moveField = (index, direction) => {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < formData.value.fields.length) {
        const field = formData.value.fields.splice(index, 1)[0]
        formData.value.fields.splice(newIndex, 0, field)
        selectedFieldIndex.value = newIndex
      }
    }

    const selectField = (index) => {
      selectedFieldIndex.value = index
      // Deselect team manager field when selecting an athlete field
      selectedTeamManagerFieldIndex.value = -1
    }

    // Field type operations
    const hasOptions = (type) => {
      return ['select', 'radio', 'checkbox'].includes(type)
    }

    const onFieldTypeChange = () => {
      if (selectedField.value) {
        if (selectedField.value.type === 'select') {
          // Dropdown uses selectEmptyOption only; hidden legacy placeholder confused empty-option text
          selectedField.value.placeholder = ''
        }
        if (
          hasOptions(selectedField.value.type) &&
          (!selectedField.value.options ||
            selectedField.value.options.length === 0)
        ) {
          selectedField.value.options = ['Option 1']
        }
        if (!hasOptions(selectedField.value.type)) {
          delete selectedField.value.options
        }
      }
    }

    const addOption = () => {
      if (selectedField.value && selectedField.value.options) {
        selectedField.value.options.push(
          `Option ${selectedField.value.options.length + 1}`
        )
      }
    }

    const moveOption = (index, direction) => {
      if (!selectedField.value || !selectedField.value.options) return
      const newIndex = index + direction
      if (
        newIndex < 0 ||
        newIndex >= selectedField.value.options.length ||
        newIndex === index
      ) {
        return
      }
      const option = selectedField.value.options.splice(index, 1)[0]
      selectedField.value.options.splice(newIndex, 0, option)
    }

    const removeOption = (index) => {
      if (selectedField.value && selectedField.value.options) {
        selectedField.value.options.splice(index, 1)
      }
    }

    // Enable manual editing of field name
    const enableManualEdit = () => {
      if (selectedField.value) {
        manuallyEditedNames.value.add(selectedField.value.id)
        // Focus the input after enabling
        nextTick(() => {
          const input = document.querySelector('.field-name-container input')
          if (input) {
            input.focus()
            input.select()
          }
        })
      }
    }

    // Disable manual editing and reset to auto-generated name
    const disableManualEdit = () => {
      if (selectedField.value) {
        manuallyEditedNames.value.delete(selectedField.value.id)
        // Regenerate name from label
        if (selectedField.value.label) {
          const baseName = generateFieldName(selectedField.value.label)
          selectedField.value.name = ensureUniqueFieldName(
            baseName,
            selectedField.value.id
          )
        }
      }
    }

    // Field name sanitization
    const sanitizeFieldName = () => {
      if (selectedField.value) {
        // Mark this field's name as manually edited (if not already)
        manuallyEditedNames.value.add(selectedField.value.id)

        const sanitized = selectedField.value.name
          .toLowerCase()
          // Allow Unicode letters, numbers, and underscores
          // Remove only problematic characters (spaces, punctuation, etc.)
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          // Replace multiple underscores with single underscore
          .replace(/_{2,}/g, '_')
          // Remove leading/trailing underscores
          .replace(/^_|_$/g, '')

        // Ensure the sanitized name is unique
        selectedField.value.name = ensureUniqueFieldName(
          sanitized,
          selectedField.value.id
        )
      }
    }

    // Auto-generate field name from label
    const generateFieldName = (label) => {
      return (
        label
          .toLowerCase()
          // Allow Unicode letters, numbers, and underscores
          // Remove only problematic characters (spaces, punctuation, etc.)
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          // Replace multiple underscores with single underscore
          .replace(/_{2,}/g, '_')
          // Remove leading/trailing underscores
          .replace(/^_|_$/g, '')
      )
    }

    // Track which fields have had their names manually edited
    const manuallyEditedNames = ref(new Set())

    // Function to ensure field name is unique
    const ensureUniqueFieldName = (baseName, excludeFieldId) => {
      const existingNames = formData.value.fields
        .filter((f) => f.id !== excludeFieldId)
        .map((f) => f.name)
        .filter((name) => name && name.trim() !== '')

      let uniqueName = baseName
      let counter = 1

      while (existingNames.includes(uniqueName)) {
        uniqueName = `${baseName}_${counter}`
        counter++
      }

      return uniqueName
    }

    // Ensure unique name within Team Manager fields (independent from athlete fields)
    const ensureUniqueTeamManagerFieldName = (baseName, excludeFieldId) => {
      const existingNames = (formData.value.settings.teamManagerFields || [])
        .filter((f) => f.id !== excludeFieldId)
        .map((f) => f.name)
        .filter((name) => name && name.trim() !== '')

      let uniqueName = baseName
      let counter = 1

      while (existingNames.includes(uniqueName)) {
        uniqueName = `${baseName}_${counter}`
        counter++
      }

      return uniqueName
    }

    // Function to handle field name auto-generation
    const updateFieldNameFromLabel = (field, label) => {
      if (
        field &&
        label &&
        field.type !== 'section' &&
        !manuallyEditedNames.value.has(field.id)
      ) {
        const baseName = generateFieldName(label)
        field.name = ensureUniqueFieldName(baseName, field.id)
      }
    }

    // Single watcher for field name auto-generation
    // Handles both label changes and field selection
    watch(
      () => ({
        field: selectedField.value,
        label: selectedField.value && selectedField.value.label,
      }),
      ({ field, label }) => {
        // Use nextTick to ensure DOM updates complete first
        nextTick(() => {
          updateFieldNameFromLabel(field, label)
        })
      },
      { immediate: true }
    )

    // Auto-generate team manager field names from label on selection/label changes
    watch(
      () => ({
        tmField: selectedTeamManagerField.value,
        label:
          selectedTeamManagerField.value &&
          selectedTeamManagerField.value.label,
      }),
      () => {
        nextTick(() => {
          updateTeamManagerFieldName()
        })
      },
      { immediate: true }
    )

    // Team Manager Field operations
    const addTeamManagerField = () => {
      const newField = {
        id: `team_field_${teamManagerFieldIdCounter.value++}`,
        type: 'text',
        label: '',
        name: '',
        placeholder: '',
        required: false,
        options: [],
        validation: {},
      }
      formData.value.settings.teamManagerFields.push(newField)
      selectedTeamManagerFieldIndex.value =
        formData.value.settings.teamManagerFields.length - 1
    }

    const removeTeamManagerField = (index) => {
      if (confirm(t('registration.deleteFieldConfirm'))) {
        formData.value.settings.teamManagerFields.splice(index, 1)
        if (
          selectedTeamManagerFieldIndex.value >=
          formData.value.settings.teamManagerFields.length
        ) {
          selectedTeamManagerFieldIndex.value =
            formData.value.settings.teamManagerFields.length - 1
        }
      }
    }

    const moveTeamManagerField = (index, direction) => {
      const newIndex = index + direction
      if (
        newIndex >= 0 &&
        newIndex < formData.value.settings.teamManagerFields.length
      ) {
        const field = formData.value.settings.teamManagerFields.splice(
          index,
          1
        )[0]
        formData.value.settings.teamManagerFields.splice(newIndex, 0, field)
        selectedTeamManagerFieldIndex.value = newIndex
      }
    }

    const selectTeamManagerField = (index) => {
      selectedTeamManagerFieldIndex.value = index
      // Deselect athlete field when selecting a team manager field
      selectedFieldIndex.value = -1
    }

    const duplicateTeamManagerField = (index) => {
      const field = formData.value.settings.teamManagerFields[index]
      const duplicated = {
        ...field,
        id: `team_field_${teamManagerFieldIdCounter.value++}`,
        name: field.name ? `${field.name}_copy` : '',
        label: field.label ? `${field.label} (Copy)` : '',
        options: Array.isArray(field.options) ? [...field.options] : undefined,
        validation:
          field.validation && typeof field.validation === 'object'
            ? { ...field.validation }
            : {},
      }
      if (
        duplicated.monthNames &&
        Array.isArray(duplicated.monthNames) &&
        duplicated.monthNames.length === 12
      ) {
        duplicated.monthNames = [...duplicated.monthNames]
      }
      formData.value.settings.teamManagerFields.splice(
        index + 1,
        0,
        duplicated
      )
      selectedTeamManagerFieldIndex.value = index + 1
    }

    const onTeamManagerFieldTypeChange = () => {
      if (selectedTeamManagerField.value) {
        if (selectedTeamManagerField.value.type === 'select') {
          selectedTeamManagerField.value.placeholder = ''
        }
        if (
          hasOptions(selectedTeamManagerField.value.type) &&
          (!selectedTeamManagerField.value.options ||
            selectedTeamManagerField.value.options.length === 0)
        ) {
          selectedTeamManagerField.value.options = ['Option 1']
        }
        if (!hasOptions(selectedTeamManagerField.value.type)) {
          delete selectedTeamManagerField.value.options
        }
      }
    }

    const addTeamManagerFieldOption = () => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.options
      ) {
        selectedTeamManagerField.value.options.push(
          `Option ${selectedTeamManagerField.value.options.length + 1}`
        )
      }
    }

    const moveTeamManagerFieldOption = (index, direction) => {
      if (
        !selectedTeamManagerField.value ||
        !selectedTeamManagerField.value.options
      ) {
        return
      }
      const newIndex = index + direction
      if (
        newIndex < 0 ||
        newIndex >= selectedTeamManagerField.value.options.length ||
        newIndex === index
      ) {
        return
      }
      const option = selectedTeamManagerField.value.options.splice(index, 1)[0]
      selectedTeamManagerField.value.options.splice(newIndex, 0, option)
    }

    const removeTeamManagerFieldOption = (index) => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.options
      ) {
        selectedTeamManagerField.value.options.splice(index, 1)
      }
    }

    const sanitizeTeamManagerFieldName = () => {
      if (selectedTeamManagerField.value) {
        const sanitized = selectedTeamManagerField.value.name
          .toLowerCase()
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          .replace(/_{2,}/g, '_')
          .replace(/^_|_$/g, '')

        selectedTeamManagerField.value.name = ensureUniqueTeamManagerFieldName(
          sanitized,
          selectedTeamManagerField.value.id
        )
      }
    }

    const updateTeamManagerFieldName = () => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.label
      ) {
        const baseName = generateFieldName(selectedTeamManagerField.value.label)
        selectedTeamManagerField.value.name = ensureUniqueTeamManagerFieldName(
          baseName,
          selectedTeamManagerField.value.id
        )
      }
    }

    // Field type labels
    const getFieldTypeLabel = (type) => {
      const typeLabels = {
        text: t('registration.fieldTypes.text'),
        email: t('registration.fieldTypes.email'),
        name: t('registration.fieldTypes.name'),
        number: t('registration.fieldTypes.number'),
        textarea: t('registration.fieldTypes.textarea'),
        select: t('registration.fieldTypes.select'),
        radio: t('registration.fieldTypes.radio'),
        checkbox: t('registration.fieldTypes.checkbox'),
        date: t('registration.fieldTypes.date'),
        time: t('registration.fieldTypes.time'),
        section: t('registration.fieldTypes.section'),
      }
      return typeLabels[type] || type
    }

    // Save form
    const saveForm = () => {
      if (isFormValid.value) {
        emit('save', formData.value)
      }
    }

    // Initialize on mount
    initializeForm()
    onMounted(() => {
      loadPublicScopeOptions()
    })
    watch(
      () => (props.form && props.form.id) || null,
      () => {
        loadPublicScopeOptions()
      }
    )
    watch(
      availablePublicScopes,
      (scopes) => {
        if (!publicScopeOptionsLoaded.value) return
        const validKeys = new Set(scopes.map((scope) => scope.key))
        const next = publicVisibleScopes.value.filter((scope) =>
          validKeys.has(getPublicScopeKey(scope.type, scope.id))
        )
        if (next.length !== publicVisibleScopes.value.length) {
          publicVisibleScopes.value = next
        }
      },
      { immediate: true }
    )

    /** First option label for dropdown: selectEmptyOption if set, else i18n default (FieldEditor empty-option placeholder). Not field.placeholder — that input is hidden for select and often stale (e.g. old text-field value). */
    const selectFieldEmptyLabel = (field) => {
      if (!field) return t('registration.selectOption')
      const fromEmpty =
        field.selectEmptyOption != null
          ? String(field.selectEmptyOption).trim()
          : ''
      return fromEmpty || t('registration.selectOption')
    }

    return {
      t,
      activeTab,
      // public registrants
      availablePublicCompetitions,
      availablePublicCups,
      hasPersistedFormId,
      publicVisibleKeys,
      publicColumnLabels,
      publicShowAllTab,
      publicVisibleScopes,
      publicFieldOptions,
      selectedPublicScopesDetailed,
      isKeySelectedElsewhere,
      addPublicKey,
      removePublicKey,
      movePublicKey,
      onColumnLabelChange,
      isPublicScopeSelected,
      togglePublicScope,
      removePublicScope,
      movePublicScope,
      formData,
      selectedFieldIndex,
      selectedField,
      editingTargetField,
      isManuallyEdited,
      editorDuplicateNames,
      onEditorTypeChange,
      sanitizeEditorName,
      enableEditorManual,
      disableEditorManual,
      addEditorOption,
      moveEditorOption,
      removeEditorOption,
      selectedTeamManagerFieldIndex,
      selectedTeamManagerField,
      isEditing,
      isFormValid,

      duplicateFieldNames,
      manuallyEditedNames,
      addField,
      removeField,
      duplicateField,
      moveField,
      selectField,
      addTeamManagerField,
      removeTeamManagerField,
      moveTeamManagerField,
      selectTeamManagerField,
      duplicateTeamManagerField,
      onTeamManagerFieldTypeChange,
      addTeamManagerFieldOption,
      removeTeamManagerFieldOption,
      sanitizeTeamManagerFieldName,
      updateTeamManagerFieldName,
      hasOptions,
      onFieldTypeChange,
      addOption,
      removeOption,
      enableManualEdit,
      disableManualEdit,
      sanitizeFieldName,
      getFieldTypeLabel,
      saveForm,
      emailFieldOptions,
      managerEmailFieldOptions,
      isEmailConfigValid,
      isTestingConnection,
      connectionTestResult,
      testEmailConnection,
      applyTranslationDefaults,
      selectFieldEmptyLabel,
    }
  },
}
</script>

<style scoped>
/* Main Layout — tokens inherited by FieldsList / FieldEditor */
.form-builder-page {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.45;
  color: var(--fb-text);
  background: var(--fb-bg);

  --fb-bg: #f4f4f5;
  --fb-surface: #ffffff;
  --fb-elevated: #fafafa;
  --fb-border: #e4e4e7;
  --fb-border-strong: #d4d4d8;
  --fb-text: #18181b;
  --fb-heading: #27272a;
  --fb-muted: #52525b;
  --fb-primary: #3f3f46;
  --fb-primary-hover: #27272a;
  --fb-focus: #52525b;
  --fb-focus-ring: rgba(82, 82, 91, 0.22);
  --fb-hover-fill: #e4e4e7;
  --fb-active-fill: #d4d4d8;
  --fb-item-selected-bg: #e4e4e7;
  --fb-item-selected-border: #a1a1aa;
  --fb-danger: #b91c1c;
  --fb-danger-hover: #991b1b;
  --fb-danger-soft: #fef2f2;
  --fb-code-bg: #e4e4e7;
}

/* Header */
.form-builder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 2rem;
  background: var(--fb-surface);
  border-bottom: 1px solid var(--fb-border);
  flex-shrink: 0;
  z-index: 10;
  border-radius: 0;
  box-shadow: none;
}

.form-builder-header h3 {
  margin: 0;
  color: var(--fb-heading);
  font-size: 1.25rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Tab Navigation */
.tab-navigation {
  background: var(--fb-surface);
  border-bottom: 1px solid var(--fb-border);
  padding: 0 2rem;
  flex-shrink: 0;
  border-radius: 0;
  box-shadow: none;
}

.tab-container {
  display: flex;
  gap: 0;
  max-width: 1200px;
  margin: 0 auto;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.15rem;
  border: none;
  background: transparent;
  color: var(--fb-muted);
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease,
    border-color 0.15s ease;
  border-bottom: 2px solid transparent;
  position: relative;
}

.tab-btn:hover {
  color: var(--fb-text);
  background-color: var(--fb-hover-fill);
}

.tab-btn.active {
  color: var(--fb-heading);
  border-bottom-color: var(--fb-heading);
  background-color: transparent;
}

.tab-icon {
  font-size: 1.1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: var(--fb-primary);
  color: #fafaf9;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--fb-primary-hover);
}

.btn-primary:disabled {
  background-color: var(--fb-border-strong);
  cursor: not-allowed;
  opacity: 0.65;
}

.btn-secondary {
  background-color: #52504d;
  color: #fafaf9;
}

.btn-secondary:hover {
  background-color: var(--fb-primary-hover);
}

.btn-danger {
  background-color: var(--fb-danger);
  color: #fafaf9;
}

.btn-danger:hover:not(:disabled) {
  background-color: var(--fb-danger-hover);
}

/* Main Content */
.form-builder-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

/* Content Sections */
.content-section {
  max-width: 1200px;
  margin: 0 auto 1rem;
  background: var(--fb-surface);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  box-shadow: none;
}

.content-section h4 {
  margin: 0 0 1.25rem 0;
  color: var(--fb-heading);
  font-size: 1.0625rem;
  font-weight: 600;
}

/* Form Grid Layouts */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.visible-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.visible-field-row {
  display: grid;
  grid-template-columns: auto 1fr 1fr auto;
  gap: 0.5rem;
  align-items: center;
}
.visible-field-row .drag-index {
  width: 2ch;
  text-align: right;
  color: var(--fb-muted);
}
.visible-field-row .label-override {
  width: 100%;
}
.visible-field-row .row-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}
.actions-row {
  margin-top: 0.5rem;
}
.hint {
  color: var(--fb-muted);
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.compact-hint {
  margin-top: 0.15rem;
}

.public-scope-selector-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 0.85rem;
}

.public-scope-card {
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  background: var(--fb-surface);
  padding: 0.8rem 0.9rem;
}

.public-scope-card-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--fb-heading);
  margin-bottom: 0.55rem;
}

.public-scope-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.public-scope-empty-state {
  color: var(--fb-muted);
  font-size: 0.9rem;
  padding: 0.35rem 0;
}

.public-selected-scopes {
  margin-top: 0.95rem;
}

.public-scope-row {
  grid-template-columns: auto minmax(0, 1fr) auto auto;
}

.public-scope-pill {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.public-scope-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.5rem;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: var(--fb-elevated);
  border: 1px solid var(--fb-border);
  color: var(--fb-muted);
  font-size: 0.75rem;
  font-weight: 600;
}

.public-scope-id {
  color: var(--fb-muted);
  font-size: 0.82rem;
}

.form-grid .full-width {
  grid-column: 1 / -1;
}

.basic-info-section,
.basic-public-section {
  max-width: 980px;
}

.basic-public-section {
  margin-top: 0.75rem;
}

.basic-info-section,
.basic-public-section {
  padding: 1.25rem 1.35rem;
  border: 1px solid var(--fb-border);
  box-shadow: none;
}

.basic-info-section h4,
.basic-public-section h4 {
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.basic-info-grid {
  gap: 0.85rem 1rem;
}

.basic-info-grid .form-group {
  margin-bottom: 0.35rem;
}

.basic-info-grid .form-group.full-width {
  margin-bottom: 0.45rem;
}

.basic-info-grid textarea {
  min-height: 96px;
}

.basic-info-grid .help-text,
.basic-info-grid .hint {
  margin-top: 0.2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.styling-group h5,
.translation-group h5 {
  margin: 0 0 1rem 0;
  color: var(--fb-heading);
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--fb-border);
}

.translation-defaults-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
}

.translations-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.translation-group {
  background: var(--fb-elevated);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 0.75rem;
}

/* Fields Tab Layout */
.fields-tab {
  display: grid !important;
  grid-template-columns: 350px 1fr !important;
  gap: 2rem !important;
  padding: 2rem !important;
  height: 100%;
  align-items: stretch;
}

.fields-left-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.fields-right-panel {
  min-width: 0;
  display: flex;
}

.fields-panel,
.field-editor-panel {
  background: var(--fb-surface);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.15rem;
  background: var(--fb-elevated);
  border-bottom: 1px solid var(--fb-border);
}

.panel-header h4 {
  margin: 0;
  color: var(--fb-heading);
  font-size: 1rem;
  font-weight: 600;
}

.field-type-badge {
  background: var(--fb-code-bg);
  color: var(--fb-muted);
  border: 1px solid var(--fb-border);
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h4 {
  margin: 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: var(--fb-heading);
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  max-width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--fb-border-strong);
  border-radius: 6px;
  font-size: 0.9375rem;
  box-sizing: border-box;
  background: var(--fb-surface);
  color: var(--fb-text);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--fb-focus);
  box-shadow: 0 0 0 2px var(--fb-focus-ring);
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal !important;
}

.checkbox-label input {
  width: auto !important;
  margin: 0;
}

.color-input {
  width: 60px !important;
  height: 40px;
  border: 1px solid var(--fb-border-strong);
  border-radius: 6px;
  padding: 2px;
  cursor: pointer;
}

.field-help {
  color: var(--fb-muted);
  font-size: 0.8125rem;
  margin-top: 0.25rem;
  display: block;
}

.field-error {
  color: var(--fb-danger);
  font-size: 0.8125rem;
  margin-top: 0.25rem;
  display: block;
  font-weight: 500;
}

.form-group input.error,
.form-group textarea.error,
.form-group select.error {
  border-color: var(--fb-danger);
  box-shadow: 0 0 0 2px rgba(185, 28, 28, 0.18);
}

.form-group input.auto-generated {
  background-color: var(--fb-elevated);
  color: var(--fb-muted);
  cursor: not-allowed;
}

.field-name-container {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.field-name-container input {
  flex: 1;
}

.edit-name-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  white-space: nowrap;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid var(--fb-border-strong);
  color: var(--fb-muted);
}

.btn-outline:hover {
  background-color: var(--fb-hover-fill);
  color: var(--fb-heading);
}

/* Empty Fields State */
.empty-fields {
  text-align: center;
  padding: 3rem 2rem;
  color: var(--fb-muted);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.empty-fields h5 {
  margin: 0 0 0.5rem 0;
  color: var(--fb-heading);
  font-size: 1.05rem;
}

.empty-fields p {
  margin: 0 0 1.5rem 0;
  color: var(--fb-muted);
}

/* Fields List */
.fields-list {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 60vh;
  overflow-y: auto;
}

.field-item {
  background: var(--fb-surface, #fff);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.field-item:hover {
  border-color: var(--fb-border-strong);
  background-color: var(--fb-hover-fill);
}

.field-item.active {
  border-color: var(--fb-item-selected-border);
  background-color: var(--fb-item-selected-bg);
  box-shadow: none;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.field-info {
  flex: 1;
}

.field-label {
  font-weight: 600;
  color: var(--fb-heading);
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
}

.field-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.field-type {
  font-size: 0.72rem;
  color: var(--fb-muted);
  text-transform: none;
  font-weight: 500;
  background: var(--fb-code-bg);
  border: 1px solid var(--fb-border);
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
}

.required-indicator {
  color: var(--fb-danger);
  font-size: 0.72rem;
  font-weight: 600;
  background: var(--fb-danger-soft);
  border: 1px solid var(--fb-danger);
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
}

.field-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

/* Field Editor */
.editor-content {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.field-type-select {
  font-weight: 600;
  color: var(--fb-heading);
}

/* Options */
.options-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option-item {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.option-item input {
  flex: 1;
  margin: 0;
  min-width: 0;
  box-sizing: border-box;
}

.add-option-btn {
  align-self: flex-start;
}

/* Validation Grid */
.validation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.validation-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.validation-item label {
  font-size: 0.8125rem;
  margin: 0;
  color: var(--fb-muted);
}

.validation-item input {
  margin: 0;
}

/* Preview Tab */
.preview-container {
  max-width: 980px;
  margin: 0 auto;
}

.form-preview {
  background: var(--fb-elevated);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 1rem;
  max-height: 62vh;
  overflow-y: auto;
}

.preview-header {
  background: var(--fb-surface);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 0.9rem;
  margin-bottom: 0.9rem;
}

.preview-header h3 {
  margin: 0 0 0.35rem 0;
  color: var(--fb-heading);
  font-size: 1.05rem;
}

.preview-header p {
  margin: 0;
  color: var(--fb-muted);
  font-size: 0.9375rem;
}

.preview-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: var(--fb-surface);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 0.8rem;
}

.preview-label {
  font-weight: 600;
  color: var(--fb-heading);
  font-size: 0.9375rem;
}

.required {
  color: var(--fb-danger);
}

.preview-input input,
.preview-input textarea,
.preview-input select {
  width: 100%;
  max-width: 100%;
  padding: 0.6rem 0.7rem;
  border: 1px solid var(--fb-border-strong);
  border-radius: 6px;
  background-color: var(--fb-elevated);
  box-sizing: border-box;
  color: var(--fb-muted);
}

.radio-preview,
.checkbox-preview {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.radio-option,
.checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.4rem;
  border: 1px solid var(--fb-border);
  border-radius: 6px;
  background: var(--fb-surface);
}

.radio-option input,
.checkbox-option input {
  width: auto !important;
  margin: 0;
  flex-shrink: 0;
}

.radio-option label,
.checkbox-option label {
  margin: 0;
  font-weight: 500;
  color: var(--fb-heading);
  flex: 1;
}

.no-fields-preview,
.no-options-message {
  color: var(--fb-muted);
  font-size: 0.9375rem;
  padding: 0.75rem;
  background-color: var(--fb-elevated);
  border: 1px dashed var(--fb-border-strong);
  border-radius: 6px;
  text-align: center;
}

.no-fields-preview p {
  margin: 0;
}

.section-preview {
  margin: 0.4rem 0;
  padding: 0.2rem 0;
}

.section-divider {
  height: 1px;
  background: var(--fb-border-strong);
  margin-bottom: 0.7rem;
  border-radius: 0;
}

.section-title {
  margin: 0 0 0.4rem 0;
  color: var(--fb-heading);
  font-size: 1rem;
  font-weight: 600;
  border-left: 3px solid var(--fb-primary);
  padding-left: 0.75rem;
}

.section-description {
  margin: 0;
  color: var(--fb-muted);
  font-size: 0.875rem;
  line-height: 1.4;
  padding-left: 0.75rem;
}

.preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--fb-border);
}

.preview-submit-btn,
.preview-reset-btn {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  opacity: 0.9;
}

.preview-submit-btn {
  background: var(--fb-primary);
  color: #fafaf9;
}

.preview-reset-btn {
  background: #52504d;
  color: #fafaf9;
}

/* Buttons */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
}

.btn-primary {
  background-color: var(--fb-primary);
  color: #fafaf9;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--fb-primary-hover);
}

.btn-primary:disabled {
  background-color: var(--fb-border-strong);
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #52504d;
  color: #fafaf9;
}

.btn-secondary:hover {
  background-color: var(--fb-primary-hover);
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  color: var(--fb-muted);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.btn-icon:hover {
  background-color: var(--fb-hover-fill);
  color: var(--fb-heading);
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon.btn-danger {
  color: var(--fb-danger);
}

.btn-icon.btn-danger:hover {
  background-color: var(--fb-danger-soft);
  color: var(--fb-danger-hover);
}

.help-text {
  font-size: 0.875rem;
  color: var(--fb-muted);
  margin-top: 0.25rem;
  margin-bottom: 0;
  line-height: 1.4;
}

.form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--fb-border-strong);
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
  background: var(--fb-surface);
  color: var(--fb-text);
}

.form-textarea:focus {
  outline: none;
  border-color: var(--fb-focus);
  box-shadow: 0 0 0 2px var(--fb-focus-ring);
}

.form-textarea:disabled {
  background-color: var(--fb-elevated);
  cursor: not-allowed;
}

.placeholder-legend {
  margin-top: 1rem;
  padding: 1rem;
  background-color: var(--fb-elevated);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
}

.placeholder-legend h5 {
  margin: 0 0 0.75rem 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--fb-heading);
}

.placeholder-grid {
  display: grid;
  gap: 0.5rem;
}

.placeholder-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.placeholder-item code {
  background-color: var(--fb-code-bg);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  color: var(--fb-heading);
  min-width: 120px;
  text-align: center;
}

.placeholder-item span {
  color: var(--fb-muted);
}

.email-settings-section {
  max-width: 980px;
}

.email-settings-grid {
  gap: 0.9rem 1rem;
}

.email-settings-grid .form-group {
  margin-bottom: 0.35rem;
}

.email-settings-grid .form-group.full-width {
  margin-bottom: 0.5rem;
}

.email-subsection-title {
  margin: 0.25rem 0 0.1rem 0;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--fb-border);
  color: var(--fb-heading);
  font-size: 0.9375rem;
  font-weight: 600;
}

.email-settings-grid .help-text {
  margin-top: 0.2rem;
}

.email-settings-grid .placeholder-legend {
  margin-top: 0.6rem;
  padding: 0.75rem;
}

.smtp-settings-card,
.sender-settings-card,
.athlete-template-card,
.manager-template-card {
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 0.65rem 0.75rem 0.55rem;
  background: var(--fb-surface);
}

.smtp-settings-card .email-subsection-title,
.sender-settings-card .email-subsection-title,
.athlete-template-card .email-subsection-title,
.manager-template-card .email-subsection-title {
  margin-top: 0;
}

.smtp-compact-grid,
.sender-compact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem 0.8rem;
}

.smtp-compact-grid .form-group,
.sender-compact-grid .form-group {
  margin-bottom: 0;
}

.smtp-compact-grid .full-span {
  grid-column: 1 / -1;
}

.compact-checkbox-label {
  margin-top: 0.15rem;
}

.connection-result {
  margin-top: 0.55rem;
  padding: 0.6rem 0.7rem;
  border-radius: 8px;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.connection-result-title {
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
}

.connection-result-message {
  font-size: 0.84rem;
  line-height: 1.35;
}

.connection-result.connection-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #b6dfc2;
}

.connection-result.connection-error {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #efb3b8;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Team Manager Fields Builder */
.team-manager-fields-builder {
  margin-top: 1rem;
  border: 1px solid var(--fb-border);
  border-radius: 8px;
  padding: 1rem;
  background-color: var(--fb-elevated);
}

.fields-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: 600;
  color: var(--fb-heading);
}

.empty-team-fields {
  text-align: center;
  padding: 2rem;
  color: var(--fb-muted);
}

.team-manager-fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1rem;
}

/* Match FieldsList.vue `.field-item` — same row chrome for athlete + team manager lists */
.team-manager-fields-list .team-field-item {
  background: var(--fb-surface, #fff);
  border: 1px solid var(--fb-border, #e4e4e7);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.team-manager-fields-list .team-field-item:hover {
  border-color: var(--fb-border-strong, #d4d4d8);
  background-color: var(--fb-hover-fill, #e4e4e7);
}

.team-manager-fields-list .team-field-item.active {
  border-color: var(--fb-item-selected-border, #a1a1aa);
  background-color: var(--fb-item-selected-bg, #e4e4e7);
  box-shadow: none;
}

.team-manager-field-actions {
  gap: 0.25rem;
}

.team-manager-field-actions .tm-field-icon-btn {
  background: var(--fb-elevated, #fafafa);
  border: 1px solid var(--fb-border, #e4e4e7);
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  color: var(--fb-muted, #52525b);
  line-height: 1;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.team-manager-field-actions .tm-field-icon-btn:hover:not(:disabled) {
  background-color: var(--fb-hover-fill, #e4e4e7);
  border-color: var(--fb-border-strong, #d4d4d8);
  color: var(--fb-heading, #27272a);
}

.team-manager-field-actions .tm-field-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.team-manager-field-actions .tm-field-svg {
  width: 15px;
  height: 15px;
  fill: currentColor;
  display: block;
}

.team-manager-field-actions .tm-field-icon-btn.btn-danger {
  color: var(--fb-danger, #b91c1c);
}

.team-manager-field-actions .tm-field-icon-btn.btn-danger:hover:not(:disabled) {
  background-color: var(--fb-danger-soft, #fef2f2);
  color: var(--fb-danger-hover, #991b1b);
}

.team-field-editor {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--fb-surface);
  border: 1px solid var(--fb-border);
  border-radius: 8px;
}

.team-field-editor h6 {
  margin: 0 0 1rem 0;
  color: var(--fb-heading);
  font-size: 1rem;
  font-weight: 600;
}

.form-builder-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: var(--fb-surface);
  border-top: 1px solid var(--fb-border);
  z-index: 10;
}

.form-builder-footer .btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.form-builder-footer .btn-footer-save {
  border: none;
  background: #166534;
  color: #fafaf9;
}

.form-builder-footer .btn-footer-save:hover:not(:disabled) {
  background: #14532d;
}

.form-builder-footer .btn-footer-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-builder-footer .btn-footer-cancel {
  border: 1px solid var(--fb-border-strong);
  background: var(--fb-hover-fill);
  color: var(--fb-heading);
}

.form-builder-footer .btn-footer-cancel:hover {
  background: var(--fb-active-fill);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .form-builder-content {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .form-builder-content {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .form-builder-header {
    padding: 1rem;
  }

  .form-builder-header h3 {
    font-size: 1.25rem;
  }

  .header-actions {
    gap: 0.5rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }

  .validation-rules {
    grid-template-columns: 1fr;
  }

  .basic-info-grid {
    grid-template-columns: 1fr;
  }

  .basic-info-section,
  .basic-public-section {
    padding: 1rem;
  }

  .form-builder-footer {
    flex-wrap: wrap;
    justify-content: stretch;
  }

  .form-builder-footer .btn {
    flex: 1;
    min-width: 120px;
  }
}
</style>
